import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import Config from "../Config.jsx";
import AuthService from "../tools/AuthService.js";
import { renderGoogleButton } from "../tools/googleIdentity.js";
import { signInWithApple, isUserCancelled } from "../tools/appleIdentity.js";
import AppleSignInButton from "./AppleSignInButton.jsx";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Paper, 
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  TextField
} from '@mui/material';
import { 
  Link as LinkIcon, 
  LinkOff as LinkOffIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';

/** Human label for a provider row. */
const PROVIDER_LABELS = {
  google: 'Google',
  apple: 'Apple',
  edconnect: 'EdConnect',
};

class EDConnectDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      edconnectAccounts: [],
      hasAccounts: false,
      // Provider links from the NEW surface (GET /api/auth/links) — Google and
      // Apple live here; the edconnect rows above stay on the legacy cmds so
      // nothing regresses while both exist.
      apiLinks: [],
      linkPassword: '',
      googleReady: false,
    };
    this.googleLinkContainer = React.createRef();
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    // When dialog opens, load EDConnect accounts
    if (this.props.open && !prevProps.open) {
      // The legacy EdConnect list is only worth fetching when its half of the
      // dialog is shown; the new links load either way.
      if (typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
          && Config.ENABLE_EDCONNECT_BINDING()) {
        this.loadEDConnectAccounts();
      }
      this.loadApiLinks();
    }
    // Mount the GIS button once its container exists (open + not yet mounted).
    if (this.props.open && !this.state.googleReady
        && this.googleLinkContainer.current
        && typeof Config.ENABLE_GOOGLE_BINDING === 'function'
        && Config.ENABLE_GOOGLE_BINDING()) {
      this.setState({ googleReady: true });
      renderGoogleButton(this.googleLinkContainer.current, this.handleGoogleLinkCredential, {
        locale: Localization.strings.getLanguage() === 'tc' ? 'zh-TW' : 'en',
      })
        .catch((e) => console.warn('EDConnectDialog: GIS failed to load', e));
    }
    if (!this.props.open && prevProps.open && this.state.googleReady) {
      // Re-mount next open — the dialog contents are unmounted on close.
      this.setState({ googleReady: false, linkPassword: '' });
    }
  }

  loadApiLinks() {
    AuthService.listLinks().then((links) => {
      this.setState({ apiLinks: Array.isArray(links) ? links : [] });
    });
  }

  handleGoogleLinkCredential(idToken) {
    // The server demands the password for any account that has one — sending
    // it here just makes the failure mode a clear message instead of a 403.
    AuthService.linkGoogle(idToken, this.state.linkPassword)
      .then((result) => {
        if (result.ok) {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_DONE, true);
          this.setState({ linkPassword: '' });
          this.loadApiLinks();
        } else if (result.status === 409) {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_CONFLICT, false);
        } else if (result.status === 403) {
          this.props.root.addNotification(Localization.strings.LINK_PASSWORD_REQUIRED, false);
        } else {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_FAILED, false);
        }
      });
  }

  handleUnlinkApi(linkId) {
    AuthService.unlinkAccount(linkId).then((ok) => {
      if (ok) {
        this.props.root.addNotification(Localization.strings.LINK_REMOVED, true);
        this.loadApiLinks();
      }
    });
  }

  loadEDConnectAccounts() {
    console.log('loadEDConnectAccounts called');
    console.log('API reference:', this.props.root.refs.API);
    
    this.setState({ loading: true });
    
    if (!this.props.root.refs.API) {
      console.error('API reference not available');
      this.setState({ loading: false });
      this.props.root.addNotification('API not available', false);
      return;
    }
    
    console.log('Making API request: get_edconnect_accounts');
    this.props.root.refs.API.request(
      "get_edconnect_accounts", 
      {}, 
      this.loadEDConnectAccountsOK, 
      this.loadEDConnectAccountsFail
    );
  }

  loadEDConnectAccountsOK(res) {
    console.log('EDConnect accounts response:', res);
    console.log('Response body:', res?.body);
    console.log('Response result:', res?.body?.result);
    
    if (res && res.body && Array.isArray(res.body.result)) {
      console.log('EDConnect accounts found:', res.body.result);
      console.log('Account count:', res.body.result.length);
      this.setState({
        loading: false,
        edconnectAccounts: res.body.result,
        hasAccounts: res.body.result.length > 0
      });
    } else {
      console.log('No EDConnect accounts found or unexpected response structure');
      console.log('Setting empty accounts array');
      this.setState({
        loading: false,
        edconnectAccounts: [],
        hasAccounts: false
      });
    }
  }

  loadEDConnectAccountsFail(err) {
    console.error('Failed to load EDConnect accounts:', err);
    console.error('Error details:', err);
    this.setState({ loading: false });
    this.props.root.addNotification('Failed to load EDConnect accounts: ' + (err?.message || 'Unknown error'), false);
  }

  handleAddAccount() {
    // Two steps, and the order is the whole point. The bearer proves WHICH
    // account this link attaches to, and a top-level navigation cannot carry
    // one — so we POST first (authenticated) to have the server seal our user
    // id into its own state, then navigate to the URL it returns.
    //
    // The PHP tried to do this in one hop with `?binding=1`, and the flag was
    // never read, so composer binding has never worked in production.
    this.setState({ loading: true });
    AuthService.startEdCityBind(window.location.href)
      .then((authorizeUrl) => {
        if (authorizeUrl) {
          window.location.href = authorizeUrl;
          return;
        }
        this.setState({ loading: false });
        this.props.root.addNotification(
          Localization.strings.EDCONNECT_BIND_FAILED, false);
      })
      .catch(() => {
        this.setState({ loading: false });
        this.props.root.addNotification(
          Localization.strings.EDCONNECT_BIND_FAILED, false);
      });
  }

  async handleAppleLinkClicked() {
    // Runs straight off the click: Apple's popup only opens inside the user
    // gesture that requested it.
    try {
      const { identityToken, rawNonce } = await signInWithApple({
        locale: Localization.strings.getLanguage(),
      });
      const result = await AuthService.linkApple(
        identityToken, rawNonce, this.state.linkPassword);
      if (result.ok) {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_DONE, true);
        this.setState({ linkPassword: '' });
        this.loadApiLinks();
      } else if (result.status === 409) {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_CONFLICT, false);
      } else if (result.status === 403) {
        this.props.root.addNotification(Localization.strings.LINK_PASSWORD_REQUIRED, false);
      } else {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_FAILED, false);
      }
    } catch (e) {
      if (isUserCancelled(e)) {
        return;
      }
      console.warn('EDConnectDialog: Apple link failed', e && (e.error || e.status));
      this.props.root.addNotification(Localization.strings.LINK_APPLE_FAILED, false);
    }
  }

  /** Is this provider already linked and active on the caller's account? */
  hasActiveLink(provider) {
    return this.state.apiLinks.some((l) => l.provider === provider && l.is_active);
  }

  handleRemoveAccount(accountId) {
    // Show confirmation dialog with proper title/content via .show()
    if (this.props.root && this.props.root.refs && this.props.root.refs.confirmDialog) {
      this.props.root.refs.confirmDialog.show(
        {
          title: Localization.strings.EDCONNECT_UNBIND_ACCOUNT,
          content: Localization.strings.EDCONNECT_CONFIRM_REMOVE,
          confirm: Localization.strings.CONFIRM,
          cancel: Localization.strings.CANCEL,
        },
        () => {
          this.removeAccount(accountId);
        },
        null
      );
    }
  }

  removeAccount(accountId) {
    this.setState({ loading: true });
    this.props.root.refs.API.request(
      "unbind_edconnect", 
      { account_id: accountId }, 
      this.removeAccountOK, 
      this.removeAccountFail
    );
  }

  removeAccountOK(res) {
    this.setState({ loading: false });
    this.props.root.addNotification(Localization.strings.EDCONNECT_UNBIND_SUCCESS, true);
    // Reload accounts
    this.loadEDConnectAccounts();
  }

  removeAccountFail(err) {
    this.setState({ loading: false });
    this.props.root.addNotification(Localization.strings.EDCONNECT_UNBIND_FAILED, false);
  }

  handleClose() {
    this.props.onClose();
  }

  formatAccountInfo(account) {
    // Display name or email or profile_id as fallback
    return account.display_name || account.email || account.profile_id || 'EDConnect Account';
  }

  formatAccountDetails(account) {
    const details = [];
    if (account.email) details.push(account.email);
    if (account.profile_id) details.push(`ID: ${account.profile_id}`);
    if (account.linked_at) {
      const linkedDate = new Date(account.linked_at);
      details.push(`Linked: ${linkedDate.toLocaleDateString()}`);
    }
    return details.join(' • ');
  }

  formatAccountType(account) {
    if (account.profile_type) {
      return account.profile_type.charAt(0).toUpperCase() + account.profile_type.slice(1);
    }
    return 'EDConnect';
  }

  render() {
    const { open } = this.props;
    const { loading, edconnectAccounts, hasAccounts } = this.state;
    const showEdc = typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
      && Config.ENABLE_EDCONNECT_BINDING();
    const showGoogleLink = typeof Config.ENABLE_GOOGLE_BINDING === 'function'
      && Config.ENABLE_GOOGLE_BINDING();
    const showAppleLink = typeof Config.ENABLE_APPLE_LOGIN === 'function'
      && Config.ENABLE_APPLE_LOGIN();
    // "Offer the button" = the provider is enabled AND not already linked. One
    // account may hold at most one identity per provider (the table's UNIQUE is
    // on (provider, profile_id), and a second link for the same provider would
    // just be a second row nobody can tell apart in the UI).
    const canLinkGoogle = showGoogleLink && !this.hasActiveLink('google');
    const canLinkApple = showAppleLink && !this.hasActiveLink('apple');
    // EdConnect rows have their own section above, so they must not count here
    // — the same filter the list below applies. API.jsx OK-path convention:
    // apiLinks can be null on a 200, so check the shape before reading it.
    const hasOtherLinks = Array.isArray(this.state.apiLinks)
      && this.state.apiLinks.some((l) => l && l.provider !== 'edconnect');

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            minHeight: '300px',
          },
        }}
      >
        <DialogTitle 
          style={{ 
            backgroundColor: this.props.theme?.palette?.primary?.main || '#ad1179', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <LinkIcon />
          {showEdc
            ? Localization.strings.EDCONNECT_BINDING
            : Localization.strings.LINKED_SIGNIN_METHODS}
        </DialogTitle>

        <DialogContent style={{ padding: '24px', minHeight: '200px' }}>
          {loading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="150px">
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: '16px' }}>
                Loading EDConnect accounts...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Legacy EdConnect half — its bind button redirects to the dead
                  PHP host, so it stays hidden until the OAuth port ships. */}
              {showEdc && (hasAccounts ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.EDCONNECT_LINKED_ACCOUNT}
                  </Typography>
                  
                  {edconnectAccounts.map((account, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      style={{
                        padding: '16px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap="12px">
                        <AccountIcon color="primary" />
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {this.formatAccountInfo(account)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {this.formatAccountDetails(account)}
                          </Typography>
                          {/* <Box display="flex" gap="8px" mt={1}>
                            <Chip 
                              icon={<LinkIcon />} 
                              label={Localization.strings.EDCONNECT_ACCOUNT_LINKED} 
                              color="success" 
                              size="small" 
                            />
                            <Chip 
                              label={this.formatAccountType(account)} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box> */}
                        </Box>
                      </Box>
                      
                      <IconButton
                        onClick={() => this.handleRemoveAccount(account.id)}
                        color="error"
                        title={Localization.strings.EDCONNECT_UNBIND_ACCOUNT}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Paper>
                  ))}
                  
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={this.handleAddAccount}
                      fullWidth
                    >
                      {Localization.strings.EDCONNECT_ADD_ACCOUNT}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <LinkOffIcon 
                    style={{ 
                      fontSize: '64px', 
                      color: '#ccc', 
                      marginBottom: '16px' 
                    }} 
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {Localization.strings.EDCONNECT_ACCOUNT_NOT_LINKED}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Connect your EDConnect account to enable single sign-on access.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={this.handleAddAccount}
                    size="large"
                  >
                    {Localization.strings.EDCONNECT_BIND_ACCOUNT}
                  </Button>
                </Box>
              ))}

              {/* Shown when a provider is linkABLE, or when the account already
                  HOLDS such a link. The second half matters when a provider is
                  switched off: gating purely on the flags would hide a Google
                  link somebody already made, leaving an active identity on the
                  account that they can neither see nor unbind. No linkable
                  provider and no existing link renders nothing at all, so the
                  section never appears as an empty password box. */}
              {(showGoogleLink || showAppleLink || hasOtherLinks) && (
                <Box mt={3}>
                  <Divider style={{ marginBottom: '16px' }} />
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.LINKED_SIGNIN_METHODS}
                  </Typography>

                  {this.state.apiLinks
                    // EdConnect rows are shown by the section above, off the
                    // legacy cmds. Listing them here as well would show the same
                    // link twice with two different unbind buttons.
                    .filter((l) => l.provider !== 'edconnect')
                    .map((link) => (
                      <Paper
                        key={'api-' + link.id}
                        elevation={1}
                        style={{
                          padding: '16px',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: link.is_active ? 1 : 0.5,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap="12px">
                          <AccountIcon color={link.is_active ? 'primary' : 'disabled'} />
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {/* A lookup, not `google ? 'Google' : 'Apple'` —
                                  that older form labelled EVERY non-Google row
                                  "Apple", so any third provider would have been
                                  silently mislabelled. */}
                              {(PROVIDER_LABELS[link.provider] || link.provider)
                                + (link.display_name ? ' — ' + link.display_name : '')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {link.is_active
                                ? 'ID: ' + link.profile_id
                                : Localization.strings.LINK_INACTIVE}
                            </Typography>
                          </Box>
                        </Box>
                        {Boolean(link.is_active) && (
                          <IconButton
                            onClick={() => this.handleUnlinkApi(link.id)}
                            color="error"
                            title={Localization.strings.LINK_REMOVE}
                          >
                            <LinkOffIcon />
                          </IconButton>
                        )}
                      </Paper>
                    ))}

                  {/* The password field is shared, but the two buttons are
                      gated INDEPENDENTLY. Gating the whole block on "Google is
                      not linked" — which is what this used to do — hid the
                      Apple button the moment a teacher linked Google, making
                      the second provider unreachable. */}
                  {(canLinkGoogle || canLinkApple) && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {Localization.strings.LINK_PASSWORD_HINT}
                      </Typography>
                      <TextField
                        type="password"
                        size="small"
                        fullWidth
                        label={Localization.strings.LINK_PASSWORD_LABEL}
                        value={this.state.linkPassword}
                        onChange={(e) => this.setState({ linkPassword: e.target.value })}
                        style={{ marginBottom: '12px' }}
                      />
                      <Box
                        style={{
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: '10px',
                        }}
                      >
                        {/* GIS renders the official Google button in here.
                            The container must stay MOUNTED whenever Google is
                            linkable — componentDidUpdate renders into the ref
                            once and never re-runs. */}
                        <div
                          ref={this.googleLinkContainer}
                          style={{ display: canLinkGoogle ? 'block' : 'none' }}
                        />
                        {canLinkApple && (
                          <AppleSignInButton
                            onClick={this.handleAppleLinkClicked}
                            label={Localization.strings.LINK_WITH_APPLE}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={this.handleClose}>
            {Localization.strings.CLOSE}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default EDConnectDialog;
